// Copyright ©️ 2026 eVolve MEP, LLC

import {
  ParentFormContext,
  useParentForm as useFormPopover,
  useCreateParentFormProvider,
} from 'components/Mantine/Modals/useParentForm';
import { WrappedPopover, type WrappedPopoverProps } from 'components/Mantine/Popovers/WrappedPopover';
import type { StrongOmit } from 'helpers/helper-types';

/** Reexported for clarity in usage */
export { useFormPopover };

export type FormPopoverProps = StrongOmit<WrappedPopoverProps, 'onConfirm' | 'formSubmit' | 'isDirty'> & {
  /** Enables the submit button even before the form has been modified (e.g. valid pre-populated defaults). */
  startDirty?: boolean;
};

/**
 * A `WrappedPopover` that collects form input, mirroring `FormModal`.
 *
 * Children must include exactly one component that calls `useFormPopover(form, onSubmit)`
 * to register its `useForm` instance.
 */
export const FormPopover = <T,>({ startDirty, ...props }: FormPopoverProps) => {
  const { closeOnConfirm, disabled, loading, onClose } = props;
  const {
    contextValue,
    formSubmit,
    isDirty,
    loading: wrappedLoading,
    disabled: wrappedDisabled,
  } = useCreateParentFormProvider<T>({ closeOnConfirm, onClose, startDirty, loading, disabled });

  return (
    <ParentFormContext value={contextValue}>
      <WrappedPopover
        {...props}
        isDirty={isDirty || startDirty}
        loading={wrappedLoading}
        disabled={wrappedDisabled}
        closeOnConfirm={false}
        formSubmit={formSubmit}
      />
    </ParentFormContext>
  );
};
